/* eslint-disable @typescript-eslint/no-explicit-any */
import { ConfigManager } from './config'
import { Customer, Theme } from './schemas'

export class APIClient {
  private config: ConfigManager

  constructor() {
    this.config = ConfigManager.getInstance()
  }

  async activateGiftCode(code: string, customer: Customer): Promise<{ giftCertificateId: string }> {
    const baseUrl = this.config.getApiUrl()
    const clientId = this.config.getClientId()

    const endpoint = `${baseUrl}/api/v2025-02/partners/nift_cards/${code}/activations`

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Client-ID': clientId,
      },
      credentials: 'include',
      body: JSON.stringify({
        loyal_customer: {
          first_name: customer.firstName,
          last_name: customer.lastName,
          email: customer.email,
          phone_number: customer.phoneNumber || '',
        },
        skip_confirmation: true,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to activate gift code: ${response.statusText}`)
    }

    const data = await response.json()

    return {
      giftCertificateId: data.data?.gift_certificate?.id || '',
    }
  }

  async checkActivation(
    code: string,
    customer: Customer
  ): Promise<{
    status: string
    theme?: Theme
    activationLink?: string
    partnerName?: string
    referralCode?: string
  }> {
    const baseUrl = this.config.getApiUrl()
    const clientId = this.config.getClientId()

    const endpoint = `${baseUrl}/api/v2025-08/partners/web_embeds/activations`

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Client-ID': clientId,
      },
      credentials: 'include',
      body: JSON.stringify({
        code: code,
        loyal_customer: {
          first_name: customer.firstName || '',
          last_name: customer.lastName || '',
          email: customer.email,
          phone_number: customer.phoneNumber || '',
        },
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to check activation: ${response.statusText}`)
    }

    const data = await response.json()

    return {
      status: data.status,
      theme: data.theme,
      activationLink: data.activation_link,
      partnerName: data.partner_name,
      referralCode: data.referral_code,
    }
  }

  async getNiftCardDetails(code: string): Promise<{
    code: string
    temp: boolean
  }> {
    const baseUrl = this.config.getApiUrl()
    const endpoint = `${baseUrl}/api/nift_cards/${code}/details`

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to get nift card details: ${response.statusText}`)
    }

    const data = await response.json()

    return {
      code: data.nift_card?.code || code,
      temp: data.nift_card?.temp || false,
    }
  }

  async confirmDetails(
    code: string,
    customer: Customer
  ): Promise<{
    code: string
    confirmed: boolean
  }> {
    const baseUrl = this.config.getApiUrl()
    const clientId = this.config.getClientId()

    const endpoint = `${baseUrl}/api/v2025-08/partners/web_embeds/confirmations/${code}`

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Client-ID': clientId,
      },
      credentials: 'include',
      body: JSON.stringify({
        loyal_customer: {
          first_name: customer.firstName,
          last_name: customer.lastName,
          email: customer.email,
          phone_number: customer.phoneNumber || '',
        },
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to confirm details: ${response.statusText}`)
    }

    const data = await response.json()

    return {
      code: data.code,
      confirmed: data.confirmed,
    }
  }

  async sendEvent(eventName: string, eventInfo?: any): Promise<void> {
    const baseUrl = this.config.getApiUrl()
    const clientId = this.config.getClientId()
    const code = this.config.getCode()
    const customer = this.config.getCustomer()
    const referralCode = this.config.getReferralCode()
    const skipOffer = this.config.getSkipOffer()
    const showClose = this.config.getShowClose()
    const countryCode = this.config.getConfig().countryCode

    const sdkConfig = {
      ...(referralCode && { referral_code: referralCode }),
      ...(skipOffer && { skip_offer: true }),
      ...(showClose && { show_close: true }),
      ...(countryCode && { country_code: countryCode }),
    }

    const endpoint = `${baseUrl}/api/v2025-08/partners/web_embeds/events`

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Client-ID': clientId,
      },
      credentials: 'include',
      body: JSON.stringify({
        code: code,
        event: {
          action_name: eventName,
          info: { ...sdkConfig, ...(eventInfo || {}) },
        },
        customer: {
          email: customer.email || '',
          phone_number: customer.phoneNumber || '',
        },
      }),
    })

    if (!response.ok) {
      console.warn(`Failed to send event ${eventName}:`, response.statusText)
    }
  }
}
